import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const documentsProtectedFormsDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['standard', 'wide']),
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    showIntegrityEvidence: z.boolean(),
});
export const documentsProtectedFormsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'documents.protected-forms.display', moduleId: 'documents',
        title: 'Protected employee form display',
        helpText: 'Controls card sizing, density, and integrity detail for protected employee forms.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: { density: 'comfortable', cardSize: 'wide', pageSize: 50, showIntegrityEvidence: false },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: documentsProtectedFormsDisplayValueSchema,
});
export const documentsProtectedFormsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(400),
    employeeSectionTitle: z.string().trim().min(1).max(120),
    employeeSectionDescription: z.string().trim().min(1).max(400),
    assetSectionTitle: z.string().trim().min(1).max(120),
    assetSectionDescription: z.string().trim().min(1).max(400),
    historySectionTitle: z.string().trim().min(1).max(120),
    formLabel: z.string().trim().min(1).max(80),
    employeeLabel: z.string().trim().min(1).max(80),
    ownEmployeeLabel: z.string().trim().min(1).max(100),
    completedPdfLabel: z.string().trim().min(1).max(100),
    signatureFileLabel: z.string().trim().min(1).max(100),
    supportingFilesLabel: z.string().trim().min(1).max(100),
    initialsLabel: z.string().trim().min(1).max(80),
    typedNameLabel: z.string().trim().min(1).max(80),
    fieldPresenceTitle: z.string().trim().min(1).max(120),
    consentLabel: z.string().trim().min(1).max(160),
    reasonLabel: z.string().trim().min(1).max(80),
    submissionReasonFallback: z.string().trim().min(10).max(500),
    assetReasonFallback: z.string().trim().min(10).max(500),
    submitAction: z.string().trim().min(1).max(80),
    submittingLabel: z.string().trim().min(1).max(120),
    createAssetAction: z.string().trim().min(1).max(80),
    appendAssetAction: z.string().trim().min(1).max(80),
    publishAssetAction: z.string().trim().min(1).max(80),
    archiveAssetAction: z.string().trim().min(1).max(80),
    restoreAssetAction: z.string().trim().min(1).max(80),
    rollbackAssetAction: z.string().trim().min(1).max(80),
    newAssetOptionLabel: z.string().trim().min(1).max(120),
    assetNameLabel: z.string().trim().min(1).max(80),
    assetKeyLabel: z.string().trim().min(1).max(80),
    assetFormatLabel: z.string().trim().min(1).max(80),
    sensitivityLabel: z.string().trim().min(1).max(80),
    sourceFileLabel: z.string().trim().min(1).max(100),
    versionLabel: z.string().trim().min(1).max(60),
    rollbackVersionLabel: z.string().trim().min(1).max(100),
    receiptFallbackTitle: z.string().trim().min(1).max(120),
    completionProofLabel: z.string().trim().min(1).max(100),
    fieldPresenceProofLabel: z.string().trim().min(1).max(100),
    formatLabels: z.strictObject({
        'fillable-pdf': z.string().trim().min(1).max(80),
        'fillable-docx': z.string().trim().min(1).max(80),
    }),
    sensitivityLabels: z.strictObject({
        confidential: z.string().trim().min(1).max(80),
        tax: z.string().trim().min(1).max(80),
        'employment-eligibility': z.string().trim().min(1).max(80),
        banking: z.string().trim().min(1).max(80),
        medical: z.string().trim().min(1).max(80),
        'personnel-restricted': z.string().trim().min(1).max(80),
    }),
    transferLabel: z.string().trim().min(1).max(120),
    loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    sourceUnavailable: z.string().trim().min(1).max(240),
    emptyForms: z.string().trim().min(1).max(240),
    emptyHistory: z.string().trim().min(1).max(240),
    savedLabel: z.string().trim().min(1).max(160),
    privacyNotice: z.string().trim().min(1).max(500),
    statusLabels: z.strictObject({
        draft: z.string().trim().min(1).max(60),
        published: z.string().trim().min(1).max(60),
        archived: z.string().trim().min(1).max(60),
    }),
});
export const documentsProtectedFormsCopySetting = definePlatformSetting({
    definition: {
        key: 'documents.copy.protected-forms', moduleId: 'documents',
        title: 'Protected employee form copy',
        helpText: 'Controls tenant-editable labels while private-data and no-email-attachment notices remain explicit.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Employee forms', title: 'Protected employee forms',
            description: 'Manage approved form files, submit completed forms, and read back redacted receipts.',
            employeeSectionTitle: 'Complete an employee form',
            employeeSectionDescription: 'Choose an approved form, upload the completed PDF, sign, and submit it to private storage.',
            assetSectionTitle: 'Approved form files',
            assetSectionDescription: 'Administrators version and publish the blank PDF or DOCX used by Workforce definitions.',
            historySectionTitle: 'Protected submission receipts', formLabel: 'Employee form',
            employeeLabel: 'Employee', ownEmployeeLabel: 'My employee record',
            completedPdfLabel: 'Completed PDF',
            signatureFileLabel: 'Signature image', supportingFilesLabel: 'Supporting files',
            initialsLabel: 'Initials', typedNameLabel: 'Typed legal name',
            fieldPresenceTitle: 'Completion check', consentLabel: 'I accept the form consent statement above.',
            reasonLabel: 'Submission or change reason',
            submissionReasonFallback: 'Submit completed protected employee form',
            assetReasonFallback: 'Maintain approved protected employee form file',
            submitAction: 'Sign and submit privately',
            submittingLabel: 'Verifying and saving protected files…', createAssetAction: 'Add approved form file',
            appendAssetAction: 'Add file version', publishAssetAction: 'Publish selected version',
            archiveAssetAction: 'Archive form file', restoreAssetAction: 'Restore form file',
            rollbackAssetAction: 'Roll back file', newAssetOptionLabel: 'Create a new approved form file',
            assetNameLabel: 'Form file name', assetKeyLabel: 'Form file key', assetFormatLabel: 'File format',
            sensitivityLabel: 'Sensitivity', sourceFileLabel: 'Blank source file',
            versionLabel: 'Version', rollbackVersionLabel: 'Rollback file version',
            receiptFallbackTitle: 'Protected employee form', completionProofLabel: 'Completion proof',
            fieldPresenceProofLabel: 'Field presence',
            formatLabels: { 'fillable-pdf': 'Fillable PDF', 'fillable-docx': 'Fillable DOCX' },
            sensitivityLabels: {
                confidential: 'Confidential', tax: 'Tax',
                'employment-eligibility': 'Employment eligibility', banking: 'Banking',
                medical: 'Medical', 'personnel-restricted': 'Personnel restricted',
            },
            transferLabel: 'Protected transfer', loadingLabel: 'Loading protected employee forms…',
            loadError: 'Protected employee forms could not be loaded.',
            mutationError: 'The protected form change could not be completed. Refresh and try again.',
            sourceUnavailable: 'Workforce form definitions are unavailable or you do not have permission to use them.',
            emptyForms: 'No published employee forms are available.',
            emptyHistory: 'No protected form receipts are available for this view.',
            savedLabel: 'The protected employee-form action completed successfully.',
            privacyNotice: 'CREST stores verified private files and redacted completion evidence. Form values are not copied into the page history, notifications, or email attachments.',
            statusLabels: { draft: 'Draft', published: 'Published', archived: 'Archived' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: documentsProtectedFormsCopyValueSchema,
});
//# sourceMappingURL=documents-protected-form-setting-definitions.js.map